/**
 * Hydrate below-fold homepage sections via Shopify Section Rendering API.
 * Placeholder nodes use [data-sondos-lazy-section="{{ section.id }}"].
 */
(function () {
  var ATTR = 'data-sondos-lazy-section';
  var LOADING = 'data-sondos-lazy-loading';

  function runScripts(container) {
    if (!container) return;
    container.querySelectorAll('script').forEach(function (oldScript) {
      var next = document.createElement('script');
      Array.prototype.forEach.call(oldScript.attributes, function (attr) {
        next.setAttribute(attr.name, attr.value);
      });
      next.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(next, oldScript);
    });
  }

  function upgradeCustomElements(scope) {
    if (!scope || !window.customElements || !customElements.upgrade) return;
    scope.querySelectorAll('slider-component, product-form, quick-add-modal, media-gallery').forEach(function (el) {
      try {
        customElements.upgrade(el);
      } catch (e) {}
    });
  }

  function hydrate(trigger) {
    var id = trigger.getAttribute(ATTR);
    if (!id || trigger.getAttribute(LOADING) === '1') return;
    trigger.setAttribute(LOADING, '1');

    var path = window.location.pathname || '/';
    var url = path + (path.indexOf('?') >= 0 ? '&' : '?') + 'section_id=' + encodeURIComponent(id);

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('section fetch failed');
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.getElementById('shopify-section-' + id);
        var current = document.getElementById('shopify-section-' + id);
        if (!incoming || !current) throw new Error('section missing');
        // This shelf owns an external sheet. Keep its existing placeholder
        // until the sheet is ready, so slow connections never see generic tabs.
        var merchStyle = incoming.querySelector('link[data-sondos-merch-style]');
        if (merchStyle) {
          return new Promise(function (resolve, reject) {
            merchStyle.onload = function () {
              merchStyle.onload = merchStyle.onerror = null;
              resolve({ incoming: incoming, current: current });
            };
            merchStyle.onerror = function () {
              merchStyle.remove();
              reject(new Error('merch stylesheet failed'));
            };
            document.head.appendChild(merchStyle);
          });
        }
        return { incoming: incoming, current: current };
      })
      .then(function (nodes) {
        var incoming = nodes.incoming;
        var current = nodes.current;
        current.replaceWith(incoming);
        runScripts(incoming);
        upgradeCustomElements(incoming);
        document.dispatchEvent(
          new CustomEvent('sondos:section:hydrated', { detail: { sectionId: id } })
        );
      })
      .catch(function () {
        trigger.removeAttribute(LOADING);
        trigger.classList.add('sondos-lazy-section--error');
      });
  }

  function init() {
    var nodes = document.querySelectorAll('[' + ATTR + ']');
    if (!nodes.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(nodes, hydrate);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          hydrate(entry.target);
        });
      },
      // Kept as the immediate path for anyone who scrolls before the idle pass
      // below has run. On its own it can never be enough: the observer only ever
      // fires while the visitor is ALREADY scrolling toward the section, so the
      // fetch — a full server round trip — sits directly in the scroll path no
      // matter how large this margin is. Raising it only buys lead time against
      // a latency we do not control. The real fix is the idle pass below.
      { rootMargin: '1200px 0px', threshold: 0.01 }
    );

    Array.prototype.forEach.call(nodes, function (node) {
      io.observe(node);
    });

    // ── Background hydration ────────────────────────────────────────────────
    // Once the page has finished loading there is nothing left to protect, so
    // hydrate whatever is still pending during idle time. The visitor then meets
    // a section whose HTML is already in the document — the same position every
    // eager section on the page starts from — instead of one that only begins a
    // round trip when they approach it.
    //
    // Why this cannot cost initial-page performance: it is gated on 'load', so
    // it cannot start until the hero LCP image and every other critical resource
    // has finished. requestIdleCallback then yields to user input, so it cannot
    // steal INP, and it adds nothing to the document, the head, or the render
    // path. The section's product images stay loading="lazy" and are still held
    // by the browser's own distance threshold, so hydrating early does not pull
    // images down for a visitor who never scrolls that far.
    //
    // hydrate() is idempotent (it latches on the LOADING attribute), so whichever
    // of the two paths fires first wins and the other is a no-op.
    function pendingNode() {
      return document.querySelector(
        '[' + ATTR + ']:not([' + LOADING + '="1"]):not(.sondos-lazy-section--error)'
      );
    }

    function schedule(fn) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(fn, { timeout: 4000 });
      } else {
        setTimeout(fn, 250);
      }
    }

    // One section per idle slice, re-querying each time: a page with several
    // lazy sections stays a trickle rather than a burst of parallel requests.
    function hydrateNextPending() {
      var node = pendingNode();
      if (!node) return;
      io.unobserve(node);
      hydrate(node);
      schedule(hydrateNextPending);
    }

    if (document.readyState === 'complete') {
      schedule(hydrateNextPending);
    } else {
      window.addEventListener(
        'load',
        function () {
          schedule(hydrateNextPending);
        },
        { once: true }
      );
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
